/// <amd-module name="Build/Admin/Pool/Components/PoolHub" />
import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { ConfirmationDialog } from "Build/Admin/Common/Components/ConfirmationDialog";
import { NotificationArea } from "Build/Admin/Common/Components/NotificationArea";
import { PoolTypeIcon } from "Build/Admin/Common/Components/PoolTypeIcon";
import { PoolHubViewKeys } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { INotification } from "Build/Admin/Common/Stores/NotificationStore";
import { isElastic, isHostedOrPoolProviderBacked, showPoolAgents } from "Build/Admin/Common/TaskAgentPoolUtils";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { PoolGraphActionsCreator } from "Build/Admin/Pool/Actions/PoolGraphActionCreator";
import { AgentAcquisitionDialog } from "Build/Admin/Pool/Components/AgentAcquisitionDialog";
import { MaintenanceHistoryContainer } from "Build/Admin/Pool/Components/MaintenanceHistoryContainer";
import { PoolAgentsContainer } from "Build/Admin/Pool/Components/PoolAgentsContainer";
import { PoolAnalytics } from "Build/Admin/Pool/Components/PoolAnalytics";
import { PoolChecksContainer } from "Build/Admin/Pool/Components/PoolChecksContainer";
import { PoolDetailsContainer } from "Build/Admin/Pool/Components/PoolDetailsContainer";
import { PoolDiagnosticsContainer } from "Build/Admin/Pool/Components/PoolDiagnosticsContainer";
import { PoolJobsContainer } from "Build/Admin/Pool/Components/PoolJobsContainer";
import { SecurityContainer } from "Build/Admin/Pool/Components/SecurityContainer";
import { SettingsContainer } from "Build/Admin/Pool/Components/SettingsContainer";
import * as Resources from "Build/Admin/Pool/Resources";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";
import { ElasticPool, TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Tab, TabBar } from "VSSUI/Tabs";
import { ChecksIssuesMessageComponent } from "Checks/ChecksOverview/ChecksIssuesMessageComponent";
import { FeatureFlagConstants, ResourceTypeNames } from "Checks/ChecksOverview/Generated/Constants";

export interface IPoolHubProps {
    actionCreator: PoolActionCreator;
    graphActionCreator: PoolGraphActionsCreator;
    storesHub: StoresHub;
}

export interface IPoolHubState {
    selectedAgentPool: TaskAgentPool | undefined;
    selectedAgentQueue: TaskAgentQueue | undefined;
    selectedElasticPool: ElasticPool | undefined;
    selectedTabId: string;
    notifications: INotification[];
    downloadAgentDialogVisible: boolean;
    agentsTabVisible: boolean;
    hasProjectData: boolean;
    hasManagePoolPermission: boolean;
}

export class PoolHub extends VssComponent<IPoolHubProps, IPoolHubState> {
    private filterToggled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private enabledAgentPoolChecksTab: boolean;
    private showCheckIssueMessageCard: boolean;

    constructor(props: IPoolHubProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getStateFromStores();
        this.enabledAgentPoolChecksTab = isFeatureFlagEnabled(context.pageContext, "DistributedTask.EnabledAgentPoolChecksTab", false);
        this.showCheckIssueMessageCard = isFeatureFlagEnabled(context.pageContext, FeatureFlagConstants.EnableChecksScalabilityResourceIssues, false);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="pipelines-pool-hub flex-grow">
                    <Header
                        title={this._getPageTitle()}
                        titleSize={TitleSize.Large}
                        titleIconProps={{
                            className: "pool-hub-title-icon",
                            render: (className?: string | undefined): JSX.Element => this._renderTitleIcon(className)
                        }}
                        commandBarItems={this._getCommandBarItems()}
                    />
                    { this._getCheckIssueCardComponent() }
                    <TabBar
                        renderAdditionalContent={this._renderAdditionalTabBarContent}
                        onSelectedTabChanged={this._onSelectedTabChanged}
                        selectedTabId={this.state.selectedTabId}
                    >
                        <Tab name={Resources.Jobs} id={PoolHubViewKeys.Jobs} />
                        {this.state.agentsTabVisible && <Tab name={Resources.Agents} id={PoolHubViewKeys.Agents} />}
                        <Tab name={Resources.Details} id={PoolHubViewKeys.Details} />
                        <Tab name={Resources.Security} id={PoolHubViewKeys.Security} />
                        {this.state.selectedAgentQueue && this.enabledAgentPoolChecksTab &&
                            <Tab name={Resources.Checks} id={PoolHubViewKeys.Checks} />
                        }
                        {this.state.selectedAgentPool && isElastic(this.state.selectedAgentPool) && (
                            <Tab name={Resources.Diagnostics} id={PoolHubViewKeys.Diagnostics} />
                        )}
                        {((!this.state.hasProjectData &&
                            this.state.selectedAgentPool &&
                            !isHostedOrPoolProviderBacked(this.state.selectedAgentPool)) ||
                            this.state.selectedElasticPool) && <Tab name={Resources.Settings} id={PoolHubViewKeys.Settings} />}
                        {!this.state.hasProjectData &&
                            this.state.selectedAgentPool &&
                            !isHostedOrPoolProviderBacked(this.state.selectedAgentPool) && (
                                <Tab name={Resources.MaintenanceHistory} id={PoolHubViewKeys.MaintenanceHistory} />
                            )}
                        {this.state.selectedAgentPool && <Tab name={Resources.Analytics} id={PoolHubViewKeys.Analytics} />}
                    </TabBar>
                    <NotificationArea notifications={this.state.notifications} onNotificationDismissed={this._onNotificationDismissed} />
                    {this._getTabContent()}
                    {this.state.downloadAgentDialogVisible && (
                        <AgentAcquisitionDialog onCancel={() => this.props.actionCreator.dismissDownloadAgentDialog()} />
                    )}
                    <ConfirmationDialog confirmationDialogStore={this.props.storesHub.confirmationDialogStore} />
                </Page>
            </Surface>
        );
    }

    private _getCheckIssueCardComponent(): React.ReactNode {
        if (this.showCheckIssueMessageCard && this.state.selectedAgentQueue){
            return <ChecksIssuesMessageComponent resourceTypeName={ResourceTypeNames.Queue} resourceIds={this.state.selectedAgentQueue.id.toString()} />;
        }
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.downloadAgentDialogStore.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.downloadAgentDialogStore.removeChangedListener(this._onStoreChanged);
    }

    private _renderAdditionalTabBarContent = (): JSX.Element | undefined => {
        switch (this.state.selectedTabId) {
            case PoolHubViewKeys.Jobs:
                if (this.props.storesHub.jobStore.state.length > 0) {
                    return <HeaderCommandBarWithFilter items={[]} filter={this.props.storesHub.jobStore.filter} filterToggled={this.filterToggled} />;
                } else {
                    return undefined;
                }
            default:
                return undefined;
        }
    };

    private _getTabContent = (): JSX.Element => {
        switch (this.state.selectedTabId) {
            case PoolHubViewKeys.Details:
                return <PoolDetailsContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Security:
                return <SecurityContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Jobs:
                return (
                    <PoolJobsContainer
                        actionCreator={this.props.actionCreator}
                        storesHub={this.props.storesHub}
                        filterToggled={this.filterToggled}
                        onFilterToggled={this._onFilterBarToggled}
                    />
                );
            case PoolHubViewKeys.Agents:
                return <PoolAgentsContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Settings:
                return <SettingsContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.MaintenanceHistory:
                return <MaintenanceHistoryContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Diagnostics:
                return <PoolDiagnosticsContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Analytics:
                return <PoolAnalytics actionCreator={this.props.graphActionCreator} storesHub={this.props.storesHub} />;
            case PoolHubViewKeys.Checks:
                return (
                    <div className="page-content page-content-top">
                        {this.state.selectedAgentQueue &&
                            <PoolChecksContainer storesHub={this.props.storesHub} />
                        }
                    </div>
                );
            default:
                return <div />;
        }
    };

    private _onFilterBarToggled = (): void => {
        this.filterToggled.value = !this.filterToggled.value;
    };

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    };

    private _getStateFromStores(): IPoolHubState {
        const projectDataService = this.context.pageContext.getService<IProjectDataService>("IProjectDataService");
        const selectedAgentPool: TaskAgentPool | undefined = this.props.storesHub.selectionStore.state.selectedAgentPool;

        return {
            selectedAgentPool,
            selectedAgentQueue: this.props.storesHub.selectionStore.state.selectedAgentQueue,
            selectedElasticPool: this.props.storesHub.selectionStore.state.selectedElasticPool,
            selectedTabId: this.props.storesHub.selectionStore.state.selectedTabId,
            notifications: this.props.storesHub.notificationStore.state,
            downloadAgentDialogVisible: this.props.storesHub.downloadAgentDialogStore.state.isOpen,
            agentsTabVisible: showPoolAgents(selectedAgentPool),
            hasProjectData: projectDataService.hasProjectData(),
            hasManagePoolPermission: this.props.storesHub.securityStore.state.hasManagePoolPermission
        } as IPoolHubState;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        this.props.actionCreator.updateSelectedTab(newTabId);
    };

    private _onNotificationDismissed = (notification: INotification): void => {
        this.props.actionCreator.notificationActionCreator.dismissNotification(notification);
    };

    private _getPageTitle(): string {
        if (this.state.selectedAgentQueue) {
            return this.state.selectedAgentQueue.name;
        }

        return this.state.selectedAgentPool ? this.state.selectedAgentPool.name : "";
    }

    private _renderTitleIcon(className?: string | undefined): JSX.Element {
        return (
            <PoolTypeIcon
                isHosted={isHostedOrPoolProviderBacked(this.state.selectedAgentPool)}
                isElastic={isElastic(this.state.selectedAgentPool)}
                className={className}
            />
        );
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        const items: IHeaderCommandBarItem[] = [];
        if (this.state.selectedAgentPool && !isHostedOrPoolProviderBacked(this.state.selectedAgentPool)) {
            const isElasticAgentPool: boolean = isElastic(this.state.selectedAgentPool);

            items.push({
                text: Resources.UpdateAllAgents,
                id: "updateAgents",
                isPrimary: isElasticAgentPool,
                onActivate: () => this._confirmUpdateAgents()
            } as IHeaderCommandBarItem);

            // Skip adding the new agent button for elastic agent pools
            if (!isElasticAgentPool) {
                items.push({
                    text: Resources.NewAgent,
                    id: "newAgent",
                    isPrimary: !isElasticAgentPool,
                    onActivate: () => this.props.actionCreator.showDownloadAgentDialog()
                } as IHeaderCommandBarItem);
            }
        }

        if (this.state.selectedAgentQueue && !this.enabledAgentPoolChecksTab) {
            items.push({
                text: Resources.Checks,
                id: "checks",
                important: false,
                iconProps: { iconName: "WaitlistConfirm" },
                onActivate: () => {
                    if (this.state.selectedAgentQueue) {
                        const linkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
                        const checksUrl = linkingService.getQueueChecksUrl(this.state.selectedAgentQueue);

                        if (checksUrl) {
                            onClickFPS(this.context.pageContext, checksUrl, true);
                        }
                    }
                }
            } as IHeaderCommandBarItem);
        }

        return items;
    }

    private _confirmUpdateAgents = (): void => {
        if (!this.state.selectedAgentPool) {
            return;
        }

        const content: JSX.Element = (
            <FormatComponent format={Resources.ConfirmUpdateAllAgentsMessage}>
                <span className="fontWeightSemiBold">{this.state.selectedAgentPool.name}</span>
                <br />
                <br />
            </FormatComponent>
        );

        this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
            this.state.selectedAgentPool,
            Resources.ConfirmUpdateTitle,
            content,
            ConfirmButtonType.Update,
            this._updateAgents,
            this._dismissConfirmationDialog
        );
    };

    private _updateAgents = (agentPool: TaskAgentPool): void => {
        this.props.actionCreator.pushAllAgentsUpdateRequest(agentPool.id);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    };

    private _dismissConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    };
}
